/**
 * Price the roles currently on the proposal form.
 *
 * Shared by the single form and the bulk step so both ask the proxy the same question in the same
 * way. The answer is the same calculation the Slack announcement and the approval card use — see
 * `proxy-server/lib/salaryBandMatch.js` — which is the point: a proposer should not be shown a
 * figure the approvers will not see.
 *
 * ⚠️ Disabled until a Role and a department are chosen. Without them there is nothing to match on,
 * and a request per keystroke of the title would be noise. A missing `Track and Level` is NOT a
 * reason to hold back: the proxy answers `no_track_and_level`, which renders as the prompt to pick
 * one, and that is more useful than an empty space where the salary will appear.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchRoleCompensation } from '../../services/internalPlaygroundApi';
import type { RoleCompensation } from '../../types/compensation';

export interface CompensationRow {
  trackAndLevel?: string;
  plannedStartDate?: string;
}

export function useRoleCompensation(role: string, peopleTeam: string, rows: CompensationRow[]) {
  const enabled = Boolean(role && peopleTeam && rows.length > 0);
  // The rows go into the key so a changed track or date refetches, and an unchanged form does not.
  // react-query hashes the key structurally, so a fresh array literal here is fine — identity does
  // not matter. It caches the answer, so stepping back and forth through the bulk form costs
  // nothing, and `keepPreviousData` stops the figures blanking between two valid states, which
  // reads as "we lost the salary" rather than "we are checking".
  //
  // ⚠️ `keepPreviousData` is why the BULK caller must check `rows.length` before joining prices to
  // rows by index — the previous answer can be a different length than the current rows.
  const key = ['internal-playground-compensation', role, peopleTeam, rows] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchRoleCompensation({ role, peopleTeam, rows }),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  // A failed request is treated as no figure rather than as an error: the proposal is still
  // fileable, and an error banner over a decoration would suggest otherwise.
  const list: RoleCompensation[] = query.data ?? [];
  return {
    rows: list,
    first: list[0] ?? null,
    /** The first costing is still in flight. Consumed as `aria-busy` on the panel's live region,
     * so a screen reader does not announce the empty state on its way to the real one. */
    isPending: enabled && query.isFetching && list.length === 0,
    /** Total fully loaded cost this year across every priced row — the figure a bulk submission
     * is actually approved on, and the one the Slack message carries. */
    totalCostUntilYearEnd: list.reduce(
      (sum, entry) => sum + (Number.isFinite(entry?.costUntilYearEnd) ? (entry.costUntilYearEnd as number) : 0),
      0
    ),
  };
}
